import {useMemo, useState} from 'react';
import {Typography} from '@mui/material';
import clsx from 'clsx';
import {t} from 'i18next';
import CloseIcon from '@mui/icons-material/Close';
import PlayIcon from '@mui/icons-material/PlayCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import './ArrResultTile.scss';
import ArrButton from '../arr-button/ArrButton';
import ArrIconButton from '../arr-icon-button/ArrIconButton';
import ArrTooltipWrapper from '../arr-tooltip-wrapper/ArrTooltipWrapper';
import ArrLoadingSpinner from '../arr-loading-spinner/ArrLoadingSpinner';

export type ArrResultTileType =
  | 'selected'
  | 'not-selected'
  | 'play'
  | 'inactive'
  | 'loading'
  | 'combined';

interface Props {
  value?: string | number;
  /** Tile caption string, maximum two line display */
  caption: string;
  /** Pass color of left side border for tile */
  color?: string;
  /** Callback event for Select Button */
  onSelect?: () => void;
  /** Callback event for UnSelect Button */
  onUnSelect?: () => void;
  /** Callback event for run Button */
  onRun?: () => void;
  className?: string;
  dataTestId: string;
  /** Type of tile for different states */
  type?: ArrResultTileType;
  /** config text which need to show for tooltip */
  toolTipText?: string;
}

const ArrResultTile: React.FC<Props> = ({
  value,
  caption,
  className = '',
  dataTestId,
  onSelect,
  onUnSelect,
  onRun,
  type = 'not-selected',
  color,
  toolTipText,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  const containerStyle = useMemo(() => {
    if (type !== 'inactive' && color) {
      return {borderLeftColor: color};
    }
    return {};
  }, [type, color]);

  const renderActionButton = useMemo(() => {
    switch (type) {
      case 'selected':
        return (
          <ArrIconButton
            icon={<CloseIcon />}
            shape="default"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onUnSelect && onUnSelect();
            }}
            ariaLabel={t('common.arrResultTile.unselectResultAriaLabel')}
            data-testid="unselect-button"
            className={clsx(
              isHovered && 'action-button-hover',
              isPressed && 'action-button-active'
            )}
            testId="arr-result-tile-unselect-result-aria-label-icon-button"
          />
        );
      case 'not-selected':
        return (
          <ArrButton
            variant="secondary"
            size="xs"
            onClick={(e) => {
              e.stopPropagation();
              onSelect && onSelect();
            }}
            aria-label={t('common.arrResultTile.selectButtonAriaLabel')}
            dataTestId={`select-button-${dataTestId}`}
            className={clsx(
              'action-button',
              isHovered && 'action-button-hover',
              isPressed && 'action-button-active'
            )}
          >
            {t('common.select')}
          </ArrButton>
        );
      case 'play':
        return (
          <ArrButton
            variant="secondary"
            size="xs"
            onClick={(e) => {
              e.stopPropagation();
              onRun && onRun();
            }}
            startIcon={<PlayIcon />}
            aria-label={t('common.arrResultTile.playButtonAriaLabel')}
            dataTestId={`play-button-${dataTestId}`}
            className={clsx(
              'action-button',
              isHovered && 'action-button-hover',
              isPressed && 'action-button-active'
            )}
          >
            {t('common.run')}
          </ArrButton>
        );
      case 'combined':
        return (
          <InfoOutlinedIcon
            aria-label="Info"
            data-testid="info-icon"
            fontSize="small"
          />
        );
      default:
        return null;
    }
  }, [type, onSelect, onRun, isHovered]);

  const renderTile = () => {
    return (
      <div
        className={clsx(
          'arr-result-tile',
          `arr-result-tile--${type}`,
          className
        )}
        data-testid={dataTestId}
        style={containerStyle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => {
          setIsHovered(false);
          setIsPressed(false);
        }}
        onMouseDown={() => setIsPressed(true)}
        onMouseUp={() => setIsPressed(false)}
        tabIndex={0}
      >
        {renderTileContent()}
      </div>
    );
  };

  const renderTileContent = () => {
    const valueComponent = renderTileValue();
    const captionComponent = renderTileCaption();

    return (
      <>
        <div
          className="top-action-container"
          data-testid={`arr-result-tile-content-${dataTestId}`}
        >
          {renderActionButton}
        </div>
        {valueComponent}
        {captionComponent}
      </>
    );
  };

  const renderTileValue = () => {
    let component = <></>;

    if (type === 'loading') {
      component = <ArrLoadingSpinner size={28} />;
    } else if (type === 'combined') {
      component = <></>;
    } else if (value || value === 0) {
      component = (
        <Typography
          className="typo-text subhead2_size value-label"
          data-testid={`arr-result-tile-value-${dataTestId}`}
        >
          {typeof value === 'number' ? value.toLocaleString() : value}
        </Typography>
      );
    } else {
      component = (
        <span
          className="empty-value-container"
          data-testid={`arr-result-tile-span`}
        />
      );
    }

    return component;
  };

  const renderTileCaption = () => {
    let className = 'caption-label';

    if (type === 'combined') {
      className = 'combined-label';
    }

    return (
      <Typography
        className={`typo-text body4_size ${className}`}
        data-testid={`arr-result-tile-caption-${dataTestId}`}
      >
        {caption}
      </Typography>
    );
  };

  return toolTipText ? (
    <ArrTooltipWrapper
      data-testid="tooltip-content-wrapper"
      trigger={renderTile()}
    >
      <span
        className="arr-result-tile-tooltip-text"
        data-testid={`arr-result-tile-${dataTestId}`}
      >
        {toolTipText}
      </span>
    </ArrTooltipWrapper>
  ) : (
    renderTile()
  );
};

export default ArrResultTile;
